import React, { useState, useEffect, useRef } from 'react';
import { MasonryPhotoAlbum } from "react-photo-album";
import "react-photo-album/masonry.css";
import './Gallery.css';
import galleryImages, { preloadImagesInBatches } from '../../data/galleryImages';

// Componente para cada imagen individual
const GalleryImage = ({ photo, imageProps, index }) => {
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const imageRef = useRef(null);

  // Detectar cuando la imagen está en el viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Manejar la carga de la imagen
  const handleImageLoad = () => {
    setLoaded(true);
  };

  // Aplicar la clase visible cuando la imagen esté cargada y sea visible
  useEffect(() => {
    if (visible && loaded && imageRef.current) {
      // Buscar el elemento padre que tiene la clase react-photo-album--photo
      const photoElement = imageRef.current.closest('.react-photo-album--photo');
      if (photoElement) {
        setTimeout(() => {
          photoElement.classList.add('visible');
        }, index * 100); // Añadir retraso basado en el índice
      }
    }
  }, [visible, loaded, index]);

  return (
    <div ref={imageRef} style={{ width: '100%', height: '100%' }}>
      <img
        {...imageProps}
        style={{ objectFit: "cover" }}
        alt={photo.alt || "Imagen de galería"}
        onLoad={handleImageLoad}
      />
    </div>
  );
};

const Gallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [photoData, setPhotoData] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const galleryRef = useRef(null);

  // Observador de intersección para detectar cuando la galería es visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (galleryRef.current) {
      observer.observe(galleryRef.current);
    }

    return () => {
      if (galleryRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Preparar las imágenes con dimensiones reales solo cuando la sección sea visible
  useEffect(() => {
    if (!isVisible) return;

    // Usar las imágenes proporcionadas como prop o las del archivo de datos
    const imagesToLoad = images || galleryImages;
    setIsLoading(true);

    // Función para obtener dimensiones reales de cada imagen
    const loadImageDimensions = async () => {
      console.log("Iniciando carga de imágenes...");
      let loadedCount = 0;
      const totalImages = imagesToLoad.length;
      const loadedPhotos = [];

      // Función para cargar una imagen individual
      const loadImage = (src, index) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            loadedCount++;
            setLoadingProgress(Math.floor((loadedCount / totalImages) * 100));

            resolve({
              src,
              key: `photo-${index}`,
              width: img.naturalWidth || 800,
              height: img.naturalHeight || 600,
              alt: `Imagen de galería ${index + 1}`
            });
          };
          img.onerror = () => {
            loadedCount++;
            setLoadingProgress(Math.floor((loadedCount / totalImages) * 100));

            // En caso de error, usar dimensiones predeterminadas
            resolve({
              src,
              key: `photo-${index}`,
              width: 800,
              height: 600,
              alt: `Imagen de galería ${index + 1}`
            });
          };
          img.src = src;
        });
      };

      // Cargar imágenes en lotes de 3
      for (let i = 0; i < totalImages; i += 3) {
        const batch = imagesToLoad.slice(i, Math.min(i + 3, totalImages));

        try {
          const batchResults = await Promise.all(
            batch.map((src, batchIndex) => loadImage(src, i + batchIndex))
          );

          loadedPhotos.push(...batchResults);

          // Actualizar el estado con las fotos cargadas hasta ahora
          setPhotoData([...loadedPhotos]);

          // Esperar antes de cargar el siguiente lote
          if (i + 3 < totalImages) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          console.log(`Cargando lote ${i / 3 + 1} de ${Math.ceil(totalImages / 3)}`);
        } catch (error) {
          console.error("Error al cargar lote de imágenes:", error);
        }
      }

      setIsLoading(false);
    };

    loadImageDimensions();
  }, [images, isVisible]);

  const openLightbox = (photo, index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <section className="gallery-section" ref={galleryRef}>
      <div className="gallery-content">
        <h2 className="text-4xl md:text-5xl font-cursive text-gray-800 mb-16 text-center">
          Nuestra Historia
        </h2>

        <div className={`gallery-container ${isVisible ? 'visible' : ''}`}>
          {photoData.length > 0 && (
            <MasonryPhotoAlbum
              photos={photoData}
              layout="masonry"
              spacing={12}
              targetRowHeight={250}
              columns={(containerWidth) => {
                if (containerWidth < 500) return 2;
                if (containerWidth < 800) return 3;
                return 4;
              }}
              componentsProps={{
                imageProps: {
                  className: "react-photo-album--image"
                },
                wrapperProps: ({ photo, index }) => ({
                  className: `react-photo-album--photo ${isVisible ? 'visible' : ''}`,
                  style: { animationDelay: `${index * 100}ms` }
                })
              }}
            />
          )}
        </div>

        {isLoading && isVisible && (
          <div className="loading-indicator">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {selectedImage !== null && photoData.length > 0 && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-button" onClick={closeLightbox}>&times;</span>
            <img
              src={photoData[selectedImage].src}
              alt={photoData[selectedImage].alt}
              style={{ objectFit: "contain" }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery; 